import { createAsyncThunk } from "@reduxjs/toolkit";

import * as basketAPI from "../../../api/basket";
import { IBasket } from "../../../models/catalogue";
import { IRootState } from "../../reducers.interfaces";
import { selectBasket, selectBasketLoaded } from "../selectors";
import { reloadBasket } from "./reloadBasket";

interface ReloadBasketIfOutdatedResult {
    isOutdated: boolean;
    basket: IBasket | null;
}

/**
 * Check basket timestamp and reload if stale.
 *
 * Compares the stored basket's last modified timestamp with the server's and
 * reloads via the reloadBasket thunk only when they differ. A basket that has
 * never been loaded is loaded straight away; a stored "no basket" answer is
 * checked with the same cheap timestamp request as a stored basket, so a
 * visitor without a basket does not pay for a full reload on every call.
 */
export const reloadBasketIfOutdated = createAsyncThunk<
    ReloadBasketIfOutdatedResult,
    void,
    { state: Pick<IRootState, "checkout"> }
>("basket/reloadBasketIfOutdated", async (_, { dispatch, getState }) => {
    const currentBasket = selectBasket(getState());
    const isLoaded = selectBasketLoaded(getState());

    const reload = async (): Promise<ReloadBasketIfOutdatedResult> => {
        const { basket } = await dispatch(reloadBasket()).unwrap();
        return {
            isOutdated: true,
            basket,
        };
    };

    if (!currentBasket) {
        if (!isLoaded) {
            return reload();
        }
        const data = await basketAPI.getLastModifiedTimestamp();
        return data ? reload() : { isOutdated: false, basket: null };
    }
    if (!currentBasket.id || !currentBasket.last_modified_timestamp) {
        return reload();
    }

    const data = await basketAPI.getLastModifiedTimestamp();
    if (!data) {
        return reload();
    }
    if (!data.last_modified_timestamp) {
        return {
            isOutdated: false,
            basket: currentBasket,
        };
    }

    const currentTimestamp = new Date(
        currentBasket.last_modified_timestamp,
    ).getTime();
    const newTimestamp = new Date(data.last_modified_timestamp).getTime();
    const idsMatch = currentBasket.id === data.id;
    const isOutdated = currentTimestamp < newTimestamp;

    if (currentTimestamp && newTimestamp && idsMatch && !isOutdated) {
        return {
            isOutdated: false,
            basket: currentBasket,
        };
    }

    return reload();
});
